import type { Locale } from '@seaa/shared'

/** Only server-configured PUBLIC_APP_URL metadata may supply this value. */
export function parseCustomerAppOrigin(value: unknown): string | null {
  if (typeof value !== 'string' || value !== value.trim()) return null
  try {
    const url = new URL(value)
    const local = ['localhost', '127.0.0.1', '[::1]'].includes(url.hostname)
    if ((url.protocol !== 'https:' && !(url.protocol === 'http:' && local))
      || url.username || url.password || url.pathname !== '/' || url.search || url.hash) return null
    return url.origin
  } catch { return null }
}

export function isCustomerPath(pathname: string) {
  try {
    const decoded = decodeURIComponent(pathname)
    return /^\/(book|confirmation|manage|waitlist)(?:\/|$)/.test(decoded) || /^\/account\/?$/.test(decoded)
  } catch { return /^\/(book|confirmation|manage|waitlist|account)(?:\/|$)/.test(pathname) }
}

function safeSegment(value: string | null): string | null {
  if (!value || value.length > 256 || /[\s/\\%?#]/u.test(value) || value === '.' || value === '..') return null
  if (Array.from(value).some((character) => character.charCodeAt(0) < 32 || character.charCodeAt(0) === 127)) return null
  return value
}

function customerPathname(pathname: string): string | null {
  if (!pathname.startsWith('/') || pathname.startsWith('//')) return null
  const parts = pathname.replace(/\/$/, '').split('/').slice(1)
  try {
    const values = parts.map((part) => safeSegment(decodeURIComponent(part)))
    if (values.some((part) => part === null)) return null
    const [page] = values
    const supported = (page === 'account' && parts.length === 1)
      || (page === 'manage' && (parts.length === 1 || parts.length === 2))
      || ((page === 'confirmation' || page === 'waitlist') && parts.length === 2)
      || (page === 'book' && (parts.length === 2 || (parts.length === 3 && ['details', 'payment'].includes(values[2]!))))
    return supported ? `/${values.map((part) => encodeURIComponent(part!)).join('/')}` : null
  } catch { return null }
}

function validLocale(value: string | null | undefined): value is Locale {
  return value === 'ar' || value === 'en' || value === 'de' || value === 'nl'
}

/** Rebuild a known customer route; never carry arbitrary redirect targets or credentials. */
export function canonicalCustomerUrl({ customerAppOrigin, pathname, search = '', defaultLocale }: {
  customerAppOrigin: unknown
  pathname: string
  search?: string
  defaultLocale?: Locale | undefined
}): string | null {
  const origin = parseCustomerAppOrigin(customerAppOrigin)
  const path = customerPathname(pathname)
  if (!origin || !path) return null
  const destination = new URL(origin)
  destination.pathname = path
  const params = new URLSearchParams(search)
  const lang = params.get('lang')
  if (validLocale(lang)) destination.searchParams.set('lang', lang)
  else if (validLocale(defaultLocale)) destination.searchParams.set('lang', defaultLocale)
  if (/^\/book\/[^/]+$/.test(path)) {
    const serviceId = safeSegment(params.get('serviceId'))
    if (serviceId) destination.searchParams.set('serviceId', serviceId)
  }
  if (path === '/manage') {
    const code = params.get('code')
    if (code && /^[a-zA-Z0-9_-]{1,64}$/.test(code)) destination.searchParams.set('code', code)
  }
  return destination.toString()
}

export function customerBookingUrl(customerAppOrigin: unknown, venueSlug: string, locale: Locale, serviceId?: string) {
  const search = new URLSearchParams({ lang: locale })
  if (serviceId) search.set('serviceId', serviceId)
  return canonicalCustomerUrl({ customerAppOrigin, pathname: `/book/${encodeURIComponent(venueSlug)}`, search: search.toString() }) ?? undefined
}

export function resolveCustomerRoute(options: Parameters<typeof canonicalCustomerUrl>[0] & { currentOrigin: string }):
  { kind: 'public' | 'ready' | 'unavailable' } | { kind: 'redirect'; location: string } {
  if (!isCustomerPath(options.pathname)) return { kind: 'public' }
  const origin = parseCustomerAppOrigin(options.customerAppOrigin)
  if (!origin) return { kind: 'unavailable' }
  if (options.currentOrigin === origin) return { kind: 'ready' }
  const location = canonicalCustomerUrl(options)
  return location ? { kind: 'redirect', location } : { kind: 'unavailable' }
}
